import React, { SetStateAction, useState } from 'react';
import { Checkbox, Dropdown } from 'flowbite-react';
import { Text } from '../text';
import { IconLoader } from '../iconLoader';
import { nestedDropdownTheme } from '../../styles/themes/nestedDropdown';

export type SubOption = { id: string; name: string; selectionTime?: number };

export type Option = {
    name: string;
    subOptions?: SubOption[];
};

export type OptionWithSelections = Option & {
    selected: SubOption[];
};

type DropdownRowProps = {
    option: OptionWithSelections;
    selectedOption: OptionWithSelections | undefined;
    setSelectedOption: React.Dispatch<SetStateAction<OptionWithSelections | undefined>>;
    canAdd: boolean;
    canRemove: boolean;
    toggleSubOption: (subOption: SubOption) => void;
    nestedMenuPosition: 'left' | 'right';
    replaceMode: boolean;
};

const DropdownRow: React.FC<DropdownRowProps> = (props) => {
    const {
        option,
        selectedOption,
        setSelectedOption,
        canAdd,
        canRemove,
        toggleSubOption,
        nestedMenuPosition,
        replaceMode,
    } = props;
    const isSelectedOption: boolean = option.name === selectedOption?.name;
    const isSelectedSubOption = (subOption: SubOption): boolean =>
        option.selected.map((so) => so.id).includes(subOption.id);

    const canSelectSubOption = (subOption: SubOption) => !isSelectedSubOption(subOption) && (canAdd || replaceMode);
    const canDeselectSubOption = (subOption: SubOption) => isSelectedSubOption(subOption) && canRemove;

    /* canToggle is dynamically computed for each subOption */
    const canToggle = (subOption: SubOption): boolean =>
        canSelectSubOption(subOption) || canDeselectSubOption(subOption);

    return (
        <Dropdown.Item className='relative bg-system-surface shrink-0'>
            <div
                className='cursor-pointer w-full text-left flex flex-row justify-between items-center'
                onClick={() => setSelectedOption(isSelectedOption ? undefined : option)}
            >
                <div className='flex flex-col'>
                    <Text variant='label-large' className='font-medium'>
                        {option.name}
                    </Text>
                    <Text variant='label-medium' className='text-system-on-surface-variant'>
                        {option.selected.length} selected
                    </Text>
                </div>
                <IconLoader
                    className='ml-4 stroke-system-primary fill-system-primary shrink-0'
                    size='sm'
                    iconName='IconChevronRight'
                />
            </div>
            {/* Nested layer */}
            {isSelectedOption && option.subOptions && (
                <div
                    className={`flex flex-col text-left w-40 max-h-56 my-1 absolute top-1/2 -translate-y-1/2 overflow-y-auto bg-system-surface shadow rounded-md z-10 ${
                        nestedMenuPosition === 'right'
                            ? /* conditional positioning on sm viewports */
                              'md:ml-2 md:left-full left-auto right-0'
                            : 'md:mr-2 md:right-full left-auto right-0'
                    }`}
                >
                    <Dropdown.Header className='shrink-0'>
                        <Text variant='body-medium' className='text-system-on-surface-variant font-semibold'>
                            {option.name}
                        </Text>
                    </Dropdown.Header>
                    {option.subOptions.map((subOption, index) => (
                        /* as='div' prevents nesting buttons (which would trigger an error) */
                        <Dropdown.Item
                            aria-label={`${option.name} - subOption ${index + 1} of ${option.subOptions?.length}`}
                            key={subOption.id}
                            as='div'
                            className={`${
                                canToggle(subOption) || isSelectedSubOption(subOption) ? 'opacity-100' : 'opacity-50'
                            } shrink-0`}
                        >
                            {/* whole row will be clickable */}
                            <div
                                className={`w-full h-full flex flex-row items-center ${
                                    canToggle(subOption) ? 'cursor-pointer' : 'cursor-default'
                                } ${replaceMode ? 'justify-between' : 'justify-start'}`}
                                onClick={() => (canToggle(subOption) ? toggleSubOption(subOption) : undefined)}
                            >
                                {!replaceMode && (
                                    <Checkbox
                                        aria-label={`${option.name} - checkbox ${index + 1}`}
                                        className={`mr-2 bg-system-surface-container checked:bg-system-primary checked:text-system-on-primary border border-system-outline focus:ring-0 focus:outline-none`}
                                        checked={option.selected.map((so) => so.id).includes(subOption.id)}
                                        disabled={!canToggle(subOption)}
                                        readOnly // as the onClick is on the parent (no onChange on checkbox may trigger a warning)
                                    />
                                )}
                                <Text variant='body-medium' className='font-medium'>
                                    {subOption.name}
                                </Text>
                                {replaceMode && option.selected.map((so) => so.id).includes(subOption.id) && (
                                    <IconLoader
                                        className='ml-2 stroke-system-primary fill-system-primary shrink-0'
                                        size='sm'
                                        iconName='IconCheck'
                                    />
                                )}
                            </div>
                        </Dropdown.Item>
                    ))}
                </div>
            )}
        </Dropdown.Item>
    );
};

export type NestedDropdownProps = {
    customLabel?: string;
    maxSelections: number;
    minSelections?: 0 | 1;
    nestedMenuPosition?: 'left' | 'right';
    smFullWidth?: boolean; // only active on sm viewports
    /* Controlled mode: parent handles state and changes */
    selectedOptions: OptionWithSelections[];
    handleChange: (selected: OptionWithSelections[]) => void;
    azSort?: boolean;
};

export const NestedDropdown: React.FC<NestedDropdownProps> = (props) => {
    const {
        customLabel,
        maxSelections,
        minSelections = 0, // can fully deselect by default
        nestedMenuPosition = 'right',
        smFullWidth = false,
        selectedOptions,
        handleChange,
        azSort = true,
    } = props;
    const [selectedOption, setSelectedOption] = useState<OptionWithSelections | undefined>();

    /* sort main options A to Z by default */
    const sortedOptions = azSort ? selectedOptions.sort((a, b) => a.name.localeCompare(b.name)) : selectedOptions;

    /* de/selection logic based on allowed min and max selections */
    const totalSelected: number = selectedOptions.reduce((acc, option) => acc + option.selected.length, 0);
    const label = customLabel ?? `${totalSelected} Parameter${totalSelected !== 1 ? 's' : ''}`;

    const replaceMode = maxSelections === 1;
    const canAdd = totalSelected < maxSelections;
    const canRemove = totalSelected > minSelections;

    const toggleSubOption = (subOption: SubOption) => {
        const updatedOptions = selectedOptions.map((option) => {
            if (replaceMode) {
                // single selection replaces previous one
                return option.name === selectedOption?.name
                    ? {
                          ...option,
                          selected: option.selected.map((so) => so.id).includes(subOption.id)
                              ? []
                              : [{ ...subOption, selectionTime: new Date().getTime() }], // remove or add
                      }
                    : // only when full deselection allowed
                      {
                          ...option,
                          selected: [],
                      };
            } else {
                // multiple selection enabled
                return option.name === selectedOption?.name
                    ? {
                          ...option,
                          selected: option.selected.map((so) => so.id).includes(subOption.id)
                              ? option.selected.filter((so) => so.id !== subOption.id) // Remove
                              : [...option.selected, { ...subOption, selectionTime: new Date().getTime() }], // Add
                      }
                    : option;
            }
        });
        handleChange(updatedOptions);
    };

    /* full width can be selected via prop (but only for sm viewports) */
    function getWidth(): string {
        return `${smFullWidth ? 'w-full' : 'w-56'}  md:w-52`;
    }
    return (
        <Dropdown
            theme={nestedDropdownTheme}
            className='font-sans'
            label={label}
            renderTrigger={() => (
                <button
                    aria-label={`Multi Select Trigger - ${label}`}
                    className={`${getWidth()} flex bg-system-surface-container text-system-on-surface px-2.5 py-2 rounded-lg focus:outline-none border border-solid border-system-outline-variant`}
                >
                    {/* workaround div with own onClick, only used to reset selectedOption without overriding button's default trigger functionality. div fills whole button in width */}
                    <div
                        className='flex flex-row justify-between w-full'
                        onClick={() => {
                            if (selectedOption) setSelectedOption(undefined);
                        }}
                    >
                        <Text variant='body-medium' className='text-system-on-surface'>
                            {label}
                        </Text>
                        <IconLoader
                            className='ml-2 stroke-system-primary fill-system-primary'
                            size='sm'
                            iconName='IconChevronDown'
                        />
                    </div>
                </button>
            )}
            dismissOnClick={false}
        >
            {/* the ul list rendered by dropdown (not visible in file tree) needs to be styled in order to look right in OFC, using CSS module */}
            {/* or: may need to edit and use top-level 'content' from theme with these ul list styles */}
            <div className={`${getWidth()} bg-system-surface z-10`}>
                <Dropdown.Header className='shrink-0'>
                    <Text variant='body-medium' className='text-system-on-surface-variant font-semibold'>
                        {totalSelected}/{maxSelections} selected
                    </Text>
                </Dropdown.Header>
                {sortedOptions.map((option, i) => (
                    <DropdownRow
                        key={option.name}
                        aria-label={`Option ${i + 1} of ${selectedOptions.length}`}
                        option={option}
                        selectedOption={selectedOption}
                        setSelectedOption={setSelectedOption}
                        canAdd={canAdd}
                        canRemove={canRemove}
                        toggleSubOption={toggleSubOption}
                        nestedMenuPosition={nestedMenuPosition}
                        replaceMode={replaceMode}
                    />
                ))}
            </div>
        </Dropdown>
    );
};
